import {
  Badge,
  Body1,
  Button,
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Text,
  Title3,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { validateAllocationShares, wageEarningItemIds } from '@worklogix/shared';
import type {
  Department,
  DepartmentAllocation,
  EmployeeEarningAssignment,
  EmployeeSupervisorMapping,
  NsPayrollItem,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import type { CreateEmployeeInput, EmployeeRow } from './api';
import { DataTable } from './components/DataTable';
import type { DataColumn } from './components/DataTable';
import { SideBlade } from './components/SideBlade';
import { EntraLinksSection } from './EntraLinksSection';
import { toErrorMessage } from './errors';
import {
  groupEarningsByEmployee,
  payrollLabel,
  summarizeEmployeeEarnings,
  toPayrollOptions,
} from './payrollOptions';
import { duplicateEmailEmployeeIds, needsAdminReasonText } from './supervisorApproval';
import { useIsAdmin } from './useIsAdmin';

const useStyles = makeStyles({
  page: {
    ...SHRINKABLE,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    // Title + action buttons cannot share one line on a phone. Without wrapping,
    // this row is wider than the screen and — being a non-scrolling child — it
    // drags the whole content region sideways, clipping everything's left edge
    // (Michael, 2026-09-10: "Employees" rendered as "ees").
    flexWrap: 'wrap',
    ...SHRINKABLE,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
  ok: {
    color: tokens.colorPaletteGreenForeground1,
  },
  nameButton: {
    justifyContent: 'flex-start',
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    fontWeight: tokens.fontWeightRegular,
  },
  splitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  pinActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  splitDept: {
    flexGrow: 1,
  },
  pctInput: {
    width: '96px',
  },
  badges: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  banner: {
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  warn: {
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  /**
   * The rule that keeps rows a uniform height: clip to one line and ellipsise.
   * Paired with a per-column width in {@link DataColumn}, without which the cell
   * has no box to be constrained by.
   */
  oneLine: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  deptCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
});

/**
 * Spread N departments evenly, to two decimals, so the shares total EXACTLY 100.
 *
 * The leftover hundredths go on the LAST row, which is Michael's own convention: for
 * Grayson Walker's three-way split he said the 33.34 "can be set to the last
 * department I listed for him". So three ways is 33.33 / 33.33 / 33.34 and five ways
 * is a clean 20 each — both of which are real splits from 2026-09-03.
 *
 * Seeding only: every share stays editable afterwards.
 */
function evenShares(departmentIds: readonly string[]): Array<{
  departmentId: string;
  percent: number;
}> {
  const n = departmentIds.length;
  if (n === 0) {
    return [];
  }
  // Work in hundredths of a percent, the precision the column stores.
  const base = Math.floor(10_000 / n);
  const leftover = 10_000 - base * n;
  return departmentIds.map((departmentId, i) => ({
    departmentId,
    percent: (base + (i === n - 1 ? leftover : 0)) / 100,
  }));
}

/** Server contract: a PIN is 4–8 digits. Never displayed or logged. */
const PIN_PATTERN = /^\d{4,8}$/;

export interface EmployeesTabProps {
  /**
   * Which slice of the roster to show. `clockable` (default) = the hourly/non-salary
   * staff who punch a clock; `salary` = the salaried, clock-exempt staff (their own
   * "Salary" tab — no PIN/kiosk columns, but a Supervisor column for the hierarchy).
   */
  scope?: 'clockable' | 'salary';
}

/**
 * Employees roster. In `clockable` scope, managers add employees and set/reset a
 * punch PIN (PINs are WRITE-ONLY end to end — masked, cleared on success, never
 * returned by the API). In `salary` scope it lists the clock-exempt salaried staff
 * with their resolved supervisor. Mirrors the FLX Loader app Users-tab PIN pattern.
 */
export function EmployeesTab({ scope = 'clockable' }: EmployeesTabProps = {}): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const isAdmin = useIsAdmin();
  const isSalary = scope === 'salary';

  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [payrollItems, setPayrollItems] = useState<NsPayrollItem[]>([]);
  // Every (employee → earning type) assignment mirrored from NetSuite. THIS is what
  // the "Payroll item" column shows; `Employee.nsPayrollItemId` holds one id and is
  // unset for nearly everyone, which is why that column used to be a wall of blanks.
  const [earnings, setEarnings] = useState<EmployeeEarningAssignment[]>([]);
  // Org-chart mapping rows, carried over from the retired Employee-mapping tab —
  // the only source of `needsAdminApproval` + its reason.
  const [mappings, setMappings] = useState<EmployeeSupervisorMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Sync from NetSuite" (org chart + payroll items + earnings), carried over from
  // the retired Employee-mapping tab.
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  // Deactivated employees are hidden by default; a header toggle reveals them.
  const [showDeactivated, setShowDeactivated] = useState(false);

  // Add-employee blade.
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState('');
  const [nsId, setNsId] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // NS-mapping blade (set ns_employee_id + payroll item for an existing employee).
  const [mapTarget, setMapTarget] = useState<EmployeeRow | null>(null);
  const [mapNsId, setMapNsId] = useState('');
  const [mapPayrollId, setMapPayrollId] = useState('');
  const [mapSaving, setMapSaving] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  // Result note from the post-mapping NS home-department sync.
  const [mapDeptNote, setMapDeptNote] = useState<string | null>(null);

  // Set-PIN blade.
  const [pinTarget, setPinTarget] = useState<EmployeeRow | null>(null);
  const [pin, setPin] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinDone, setPinDone] = useState(false);

  // Departments (drive the department-split picker).
  const [departments, setDepartments] = useState<Department[]>([]);

  // Department-assignment / split detail blade.
  const [splitTarget, setSplitTarget] = useState<EmployeeRow | null>(null);
  const [splitEnabled, setSplitEnabled] = useState(false);
  // Plain membership (split OFF): the departments the employee may clock in at.
  const [memberDeptIds, setMemberDeptIds] = useState<string[]>([]);
  // Two-department % split (split ON).
  const [splitRows, setSplitRows] = useState<Array<{ departmentId: string; percent: number }>>([]);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitSaving, setSplitSaving] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);

  const reload = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .listEmployees()
      .then((rows) => {
        if (active) {
          setEmployees(rows);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => reload(), [reload]);

  /**
   * The NetSuite-derived side data: the payroll-item catalogue (drives the picker),
   * every employee's held earnings (drives the "Payroll item" column) and the
   * org-chart mapping rows (drive the approval badges).
   *
   * Each is independently NON-FATAL: the roster is the point of this page and must
   * still render if one of these 403s or the sync has never run. A failed earnings
   * load leaves the column falling back to the legacy single mapping rather than
   * blanking the page.
   */
  const reloadNsData = useCallback(() => {
    let active = true;
    void api
      .listPayrollItems()
      .then((items) => {
        if (active) {
          setPayrollItems(items);
        }
      })
      .catch(() => {
        if (active) {
          setPayrollItems([]);
        }
      });
    void api
      .listEmployeeEarnings()
      .then((rows) => {
        if (active) {
          setEarnings(rows);
        }
      })
      .catch(() => {
        if (active) {
          setEarnings([]);
        }
      });
    void api
      .listEmployeeMappings()
      .then((rows) => {
        if (active) {
          setMappings(rows);
        }
      })
      .catch(() => {
        if (active) {
          setMappings([]);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => reloadNsData(), [reloadNsData]);

  const payrollOptions = useMemo(() => toPayrollOptions(payrollItems), [payrollItems]);

  /** Held earnings indexed by employee id — one pass, then O(1) per roster row. */
  const earningsByEmployee = useMemo(() => groupEarningsByEmployee(earnings), [earnings]);

  /**
   * The payroll items that pay WORKED TIME. Reached through a function so the
   * earnings catalogue is only pulled into the bundle where it is actually used.
   */
  const wageItemIds = useMemo(() => wageEarningItemIds(), []);

  /** employeeId → why their approvals fall to an Admin override (mapping rows only). */
  const needsAdminReasonById = useMemo(() => {
    const m = new Map<string, EmployeeSupervisorMapping['needsAdminReason']>();
    for (const row of mappings) {
      if (row.needsAdminApproval) {
        m.set(row.employeeId, row.needsAdminReason);
      }
    }
    return m;
  }, [mappings]);

  /**
   * Employees sharing an email with another employee — the create-vs-annotate
   * duplicate risk. Computed from the roster (not the mapping endpoint) so it
   * survives that endpoint failing.
   */
  const dupeEmails = useMemo(
    () => duplicateEmailEmployeeIds(employees.map((e) => ({ employeeId: e.id, email: e.email }))),
    [employees]
  );

  /**
   * Re-pull everything NetSuite-derived, in the order the data depends on it:
   * the org chart first (so an employee row exists), then the payroll-item
   * catalogue, then each employee's earnings (which need both).
   *
   * READ-ONLY against NetSuite — it queries and mirrors; it writes nothing back.
   * Locally the earnings mirror is a FULL REPLACE, so a removed earning disappears.
   */
  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const emp = await api.syncEmployees();
      const items = await api.syncPayrollItems();
      const earn = await api.syncEmployeeEarnings();
      const parts = [
        `${emp.upserted} employee${emp.upserted === 1 ? '' : 's'} touched ` +
          `(${emp.created} created, ${emp.annotated} annotated)`,
        `${items.upserted} payroll item${items.upserted === 1 ? '' : 's'} cached`,
        `${earn.linked} earning assignment${earn.linked === 1 ? '' : 's'} across ` +
          `${earn.employees} employee${earn.employees === 1 ? '' : 's'} ` +
          `(${earn.withRate} with a rate)`,
      ];
      if (earn.skippedUnknownEmployee > 0) {
        parts.push(
          `${earn.skippedUnknownEmployee} assignment${
            earn.skippedUnknownEmployee === 1 ? '' : 's'
          } skipped — no WorkLogix employee row`
        );
      }
      setSyncMessage(`Sync complete: ${parts.join('; ')}.`);
      reload();
      reloadNsData();
    } catch (err: unknown) {
      setSyncMessage(toErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  }, [api, reload, reloadNsData]);

  // Departments for the split picker (non-fatal on failure — the roster still loads).
  useEffect(() => {
    let active = true;
    void api
      .listDepartments()
      .then((ds) => {
        if (active) {
          setDepartments(ds);
        }
      })
      .catch(() => {
        if (active) {
          setDepartments([]);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  const deptName = useCallback(
    (id: string): string => departments.find((d) => d.id === id)?.name ?? '',
    [departments]
  );

  const openSplit = useCallback(
    (e: EmployeeRow) => {
      setSplitTarget(e);
      setSplitEnabled(false);
      setMemberDeptIds([]);
      setSplitRows([
        { departmentId: '', percent: 50 },
        { departmentId: '', percent: 50 },
      ]);
      setSplitError(null);
      setSplitLoading(true);
      void api
        .getEmployeeAllocations(e.id)
        .then(({ splitEnabled: se, allocations }) => {
          setSplitEnabled(se);
          setMemberDeptIds(allocations.map((a) => a.departmentId));
          if (se) {
            setSplitRows(
              allocations
                .slice(0, 2)
                .map((a) => ({ departmentId: a.departmentId, percent: a.percent ?? 50 }))
            );
          }
        })
        .catch((err: unknown) => setSplitError(toErrorMessage(err)))
        .finally(() => setSplitLoading(false));
    },
    [api]
  );

  const closeSplit = useCallback(() => {
    setSplitTarget(null);
    setSplitEnabled(false);
    setMemberDeptIds([]);
    setSplitRows([]);
    setSplitError(null);
    setSplitSaving(false);
    setSplitLoading(false);
  }, []);

  // Toggle between plain membership (any number of departments) and a PERCENTAGE
  // split, seeding each mode from the other so a chosen department is not lost.
  const toggleSplit = useCallback(
    (enabled: boolean) => {
      setSplitEnabled(enabled);
      if (enabled) {
        // Seed from every department the employee is already a member of (at least
        // two rows), spread evenly — which is what most of these splits are.
        const seeds = memberDeptIds.length >= 2 ? memberDeptIds : [memberDeptIds[0] ?? '', ''];
        setSplitRows(evenShares(seeds));
      } else {
        setMemberDeptIds(splitRows.map((r) => r.departmentId).filter((idv) => idv !== ''));
      }
    },
    [memberDeptIds, splitRows]
  );

  /** Add a row, then re-spread evenly so the total stays 100 without hand-editing. */
  const addSplitRow = useCallback(() => {
    setSplitRows((prev) => evenShares([...prev.map((r) => r.departmentId), '']));
  }, []);

  /** Remove a row, re-spreading the remainder. Never drops below two. */
  const removeSplitRow = useCallback((index: number) => {
    setSplitRows((prev) =>
      prev.length <= 2
        ? prev
        : evenShares(prev.filter((_, i) => i !== index).map((r) => r.departmentId))
    );
  }, []);

  const splitSum = splitRows.reduce((s, r) => s + (Number.isFinite(r.percent) ? r.percent : 0), 0);
  /**
   * Why the split can be rejected, in the SAME words the API uses — the rule lives in
   * `validateAllocationShares` so the two cannot drift. Null = fine.
   *
   * Departments still have to be chosen before the shared rule can judge anything, so
   * that one check stays here: a blank picker is a half-filled form, not an invalid
   * split, and "choose a department" is the more useful thing to say.
   */
  const splitProblem = useMemo((): string | null => {
    if (!splitEnabled) {
      // Membership only: any number of departments, including none (= cannot clock in).
      return null;
    }
    if (splitRows.some((r) => r.departmentId === '')) {
      return 'Choose a department for every row.';
    }
    return validateAllocationShares(
      splitRows.map((r) => ({
        departmentId: r.departmentId,
        nsDepartmentId: null,
        percent: r.percent,
      }))
    );
  }, [splitEnabled, splitRows]);
  const splitValid = splitProblem === null;

  const handleSaveSplit = useCallback(async () => {
    if (splitTarget === null || !splitValid) {
      return;
    }
    setSplitSaving(true);
    setSplitError(null);
    const payload: DepartmentAllocation[] = splitEnabled
      ? splitRows.map((r) => ({ departmentId: r.departmentId, percent: r.percent }))
      : memberDeptIds.map((idv) => ({ departmentId: idv, percent: null }));
    try {
      const result = await api.setEmployeeAllocations(splitTarget.id, splitEnabled, payload);
      setEmployees((prev) =>
        prev.map((e) =>
          e.id === splitTarget.id
            ? {
                ...e,
                splitEnabled: result.splitEnabled,
                departmentIds: result.allocations.map((a) => a.departmentId),
              }
            : e
        )
      );
      closeSplit();
    } catch (err: unknown) {
      setSplitError(toErrorMessage(err));
      setSplitSaving(false);
    }
  }, [api, splitTarget, splitEnabled, splitRows, memberDeptIds, splitValid, closeSplit]);

  const openMap = useCallback((e: EmployeeRow) => {
    setMapTarget(e);
    setMapNsId(e.nsEmployeeId ?? '');
    setMapPayrollId(e.nsPayrollItemId ?? '');
    setMapError(null);
    setMapDeptNote(null);
  }, []);

  const closeMap = useCallback(() => {
    setMapTarget(null);
    setMapNsId('');
    setMapPayrollId('');
    setMapSaving(false);
    setMapError(null);
    setMapDeptNote(null);
  }, []);

  const handleSaveMap = useCallback(async () => {
    if (mapTarget === null) {
      return;
    }
    setMapSaving(true);
    setMapError(null);
    setMapDeptNote(null);
    const trimmedNs = mapNsId.trim();
    try {
      const updated = await api.updateEmployeeMapping(mapTarget.id, {
        nsEmployeeId: trimmedNs === '' ? null : trimmedNs,
        nsPayrollItemId: mapPayrollId === '' ? null : mapPayrollId,
      });
      setEmployees((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      if (trimmedNs === '') {
        // Cleared the NS link — nothing to sync a department from.
        closeMap();
        return;
      }
      // Also mirror the NetSuite home department onto the employee's allocation.
      try {
        const dept = await api.syncEmployeeDepartment(mapTarget.id);
        setMapDeptNote(
          dept.status === 'synced'
            ? `Department synced from NetSuite: ${dept.departmentName ?? ''}.`
            : `Mapping saved. Department not synced — ${dept.reason ?? 'unknown reason'}.`
        );
      } catch (err: unknown) {
        setMapDeptNote(`Mapping saved, but the department sync failed: ${toErrorMessage(err)}`);
      }
      setMapSaving(false);
    } catch (err: unknown) {
      setMapError(toErrorMessage(err));
      setMapSaving(false);
    }
  }, [api, mapTarget, mapNsId, mapPayrollId, closeMap]);

  const closeAdd = useCallback(() => {
    setAddOpen(false);
    setName('');
    setNsId('');
    setAddSaving(false);
    setAddError(null);
  }, []);

  const handleAdd = useCallback(async () => {
    setAddSaving(true);
    setAddError(null);
    const input: CreateEmployeeInput = { displayName: name.trim() };
    if (nsId.trim() !== '') {
      input.nsEmployeeId = nsId.trim();
    }
    try {
      await api.createEmployee(input);
      closeAdd();
      reload();
    } catch (err: unknown) {
      setAddError(toErrorMessage(err));
      setAddSaving(false);
    }
  }, [api, name, nsId, closeAdd, reload]);

  const openPin = useCallback((e: EmployeeRow) => {
    setPinTarget(e);
    setPin('');
    setPinError(null);
    setPinDone(false);
  }, []);

  const closePin = useCallback(() => {
    // Clear the transient PIN from memory on close.
    setPin('');
    setPinTarget(null);
    setPinError(null);
    setPinSaving(false);
    setPinDone(false);
  }, []);

  const pinInvalid = pin.length > 0 && !PIN_PATTERN.test(pin);

  const handleSetPin = useCallback(async () => {
    if (pinTarget === null || !PIN_PATTERN.test(pin)) {
      return;
    }
    setPinSaving(true);
    setPinError(null);
    try {
      await api.setEmployeePin(pinTarget.id, pin);
      // Reflect the new PIN status in the roster immediately. setEmployeePin returns
      // no body (204), so update optimistically — otherwise the row's "No PIN" badge
      // stays stale until a full page reload (the bug this fixes).
      setEmployees((prev) => prev.map((e) => (e.id === pinTarget.id ? { ...e, hasPin: true } : e)));
      // Never echo the PIN; just confirm and drop it from state.
      setPin('');
      setPinDone(true);
    } catch (err: unknown) {
      setPinError(toErrorMessage(err));
    } finally {
      setPinSaving(false);
    }
  }, [api, pinTarget, pin]);

  // Admin-only: remove an employee's PIN (retire a temp/test credential). Updates
  // the roster optimistically so the badge flips to "No PIN" without a reload.
  const handleRemovePin = useCallback(
    async (e: EmployeeRow): Promise<void> => {
      setError(null);
      try {
        await api.removeEmployeePin(e.id);
        setEmployees((prev) => prev.map((r) => (r.id === e.id ? { ...r, hasPin: false } : r)));
      } catch (err: unknown) {
        setError(toErrorMessage(err));
      }
    },
    [api]
  );

  // Activate / deactivate an employee (optimistic; reverts on failure).
  const handleSetActive = useCallback(
    async (id: string, active: boolean) => {
      setEmployees((prev) => prev.map((e) => (e.id === id ? { ...e, active } : e)));
      try {
        await api.setEmployeeActive(id, active);
      } catch (err: unknown) {
        setError(toErrorMessage(err));
        setEmployees((prev) => prev.map((e) => (e.id === id ? { ...e, active: !active } : e)));
      }
    },
    [api]
  );

  // ns_employee_id → display name, to resolve the Supervisor column across the FULL
  // roster (a salaried person's supervisor may live in the other tab, e.g. Joseph).
  const supervisorNameByNsId = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employees) {
      if (e.nsEmployeeId !== null) {
        m.set(e.nsEmployeeId, e.displayName);
      }
    }
    return m;
  }, [employees]);

  // This tab's slice: salaried on the Salary tab, everyone else on Employees;
  // deactivated employees are hidden unless the "Show deactivated" toggle is on.
  const visibleRows = useMemo(
    () =>
      employees.filter(
        (e) =>
          (isSalary ? e.payType === 'salary' : e.payType !== 'salary') &&
          (showDeactivated || e.active)
      ),
    [employees, isSalary, showDeactivated]
  );

  /**
   * How many visible employees have NO WAGE earning — the actionable number, and
   * deliberately stricter than "no earnings at all": someone holding only a BONUS
   * or HOLIDAY item still has nothing for their clocked hours to post under, so
   * counting only the completely-empty employees understated the gap.
   */
  const noWageCount = useMemo(
    () =>
      visibleRows.filter((e) => {
        const held = earningsByEmployee.get(e.id) ?? [];
        return !held.some((a) => wageItemIds.has(a.nsPayrollItemId));
      }).length,
    [visibleRows, earningsByEmployee, wageItemIds]
  );

  const columns: ReadonlyArray<DataColumn<EmployeeRow>> = useMemo(() => {
    /**
     * EVERY cell here is deliberately ONE LINE, width-bounded, with the full value
     * on hover. Before this, three columns wrapped to three or four lines each and
     * the roster was unreadable: row heights varied per employee, and long emails
     * rendered straight over the Active checkbox because no column had a width.
     *
     * Nothing was dropped to achieve it — the flags and markers that had their own
     * columns moved INTO the cell they belong to (Split into Departments, the
     * approval flags next to the Name, the PIN badge next to its own buttons),
     * taking the roster from twelve columns to nine.
     */
    const nameCol: DataColumn<EmployeeRow> = {
      columnId: 'name',
      header: 'Name',
      width: 230,
      minWidth: 160,
      renderCell: (e) => {
        // Approval-health flags sit BESIDE the name rather than in a column of
        // their own: they are about this person, they are usually absent, and a
        // whole column of em-dashes to say so was most of the clutter.
        const flags: JSX.Element[] = [];
        if (needsAdminReasonById.has(e.id)) {
          flags.push(
            <Tooltip
              key="needsAdmin"
              content={needsAdminReasonText(needsAdminReasonById.get(e.id))}
              relationship="label"
            >
              <Badge appearance="filled" color="warning" size="small">
                Needs admin
              </Badge>
            </Tooltip>
          );
        }
        if (dupeEmails.has(e.id)) {
          flags.push(
            <Tooltip
              key="dupe"
              content="Another employee shares this email — likely a duplicate (a manual PIN row vs a sync-created node). Collapse them by setting the NetSuite id on the correct row; they are never auto-merged."
              relationship="label"
            >
              <Badge appearance="tint" color="danger" size="small">
                Duplicate
              </Badge>
            </Tooltip>
          );
        }
        const label = isSalary ? (
          <span className={styles.oneLine} title={e.displayName}>
            {e.displayName}
          </span>
        ) : (
          <Button
            appearance="transparent"
            className={mergeClasses(styles.nameButton, styles.oneLine)}
            onClick={() => openSplit(e)}
            title={`${e.displayName} — edit departments`}
          >
            {e.displayName}
          </Button>
        );
        return flags.length === 0 ? (
          label
        ) : (
          <span className={styles.nameCell}>
            {label}
            {flags}
          </span>
        );
      },
    };
    const nsCol: DataColumn<EmployeeRow> = {
      columnId: 'ns',
      header: 'NetSuite id',
      width: 92,
      minWidth: 80,
      renderCell: (e) => <span className={styles.oneLine}>{e.nsEmployeeId ?? '—'}</span>,
    };
    /**
     * What this employee's CLOCKED HOURS are paid under — their wage earning.
     *
     * It used to list every payroll item on their NetSuite file, which is items of
     * every kind at once (BONUS, HOLIDAY, ER HEALTH COST, expense reimbursement…)
     * and ran to three or four wrapped lines of mostly-irrelevant text. The full
     * list is still there on hover; the cell now answers the question the column
     * is actually asking.
     */
    const payrollCol: DataColumn<EmployeeRow> = {
      columnId: 'payroll',
      header: 'Wage earning',
      width: 230,
      minWidth: 150,
      renderCell: (e) => {
        const cell = summarizeEmployeeEarnings(
          earningsByEmployee.get(e.id) ?? [],
          wageItemIds,
          payrollLabel(payrollItems, e.nsPayrollItemId)
        );
        return (
          <span
            className={mergeClasses(styles.oneLine, cell.none ? styles.warn : undefined)}
            title={cell.detail}
          >
            {cell.label}
          </span>
        );
      },
    };
    const emailCol: DataColumn<EmployeeRow> = {
      columnId: 'email',
      header: 'Email',
      width: 200,
      minWidth: 120,
      renderCell: (e) => (
        <span className={styles.oneLine} title={e.email ?? undefined}>
          {e.email ?? '—'}
        </span>
      ),
    };
    const supervisorCol: DataColumn<EmployeeRow> = {
      columnId: 'supervisor',
      header: 'Supervisor',
      width: 150,
      minWidth: 110,
      renderCell: (e) => {
        const label =
          e.nsSupervisorId === null
            ? '— none'
            : (supervisorNameByNsId.get(e.nsSupervisorId) ?? e.nsSupervisorId);
        return (
          <span className={styles.oneLine} title={label}>
            {label}
          </span>
        );
      },
    };
    const activeCol: DataColumn<EmployeeRow> = {
      columnId: 'active',
      header: 'Active',
      width: 64,
      minWidth: 56,
      renderCell: (e) => (
        <Checkbox
          checked={e.active}
          aria-label={e.active ? 'Deactivate employee' : 'Activate employee'}
          onChange={(_, d) => void handleSetActive(e.id, d.checked === true)}
        />
      ),
    };
    const mapCol: DataColumn<EmployeeRow> = {
      columnId: 'map',
      header: '',
      width: 132,
      minWidth: 120,
      renderCell: (e) => (
        <Button size="small" onClick={() => openMap(e)}>
          Map to NetSuite
        </Button>
      ),
    };

    if (isSalary) {
      // Salaried are clock-exempt: no PIN / kiosk-membership / split columns.
      return [nameCol, nsCol, payrollCol, emailCol, supervisorCol, activeCol, mapCol];
    }

    /** Departments the employee may clock at, plus the split marker. */
    const deptsCol: DataColumn<EmployeeRow> = {
      columnId: 'departments',
      header: 'Departments',
      width: 210,
      minWidth: 140,
      renderCell: (e) => {
        const names = e.departmentIds
          .map((d) => deptName(d))
          .filter(Boolean)
          .join(', ');
        return (
          <span className={styles.deptCell}>
            <span className={styles.oneLine} title={names === '' ? undefined : names}>
              {names === '' ? '—' : names}
            </span>
            {/*
              The split marker rides along here instead of owning a column: it is a
              property OF this department assignment, and it is set for a handful of
              people, so its own column was ninety em-dashes.
            */}
            {e.splitEnabled ? (
              <Tooltip
                content="Percentage split — hours are divided across two departments at posting."
                relationship="label"
              >
                <Badge appearance="tint" color="brand" size="small">
                  Split
                </Badge>
              </Tooltip>
            ) : null}
          </span>
        );
      },
    };
    /** PIN status and its actions, in ONE cell — they used to be two columns. */
    const pinCol: DataColumn<EmployeeRow> = {
      columnId: 'pin',
      header: 'PIN',
      width: 210,
      minWidth: 180,
      renderCell: (e) => (
        <span className={styles.pinActions}>
          <Badge appearance="tint" color={e.hasPin ? 'success' : 'warning'} size="small">
            {e.hasPin ? 'Set' : 'None'}
          </Badge>
          <Button size="small" onClick={() => openPin(e)}>
            {e.hasPin ? 'Reset' : 'Set PIN'}
          </Button>
          {e.hasPin && isAdmin ? (
            <Button size="small" appearance="subtle" onClick={() => void handleRemovePin(e)}>
              Remove
            </Button>
          ) : null}
        </span>
      ),
    };
    return [
      nameCol,
      nsCol,
      payrollCol,
      emailCol,
      deptsCol,
      supervisorCol,
      activeCol,
      pinCol,
      mapCol,
    ];
  }, [
    isSalary,
    isAdmin,
    openPin,
    handleRemovePin,
    openMap,
    openSplit,
    deptName,
    payrollItems,
    earningsByEmployee,
    wageItemIds,
    needsAdminReasonById,
    dupeEmails,
    styles,
    supervisorNameByNsId,
    handleSetActive,
  ]);

  const canAdd = name.trim().length > 0 && !addSaving;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>{isSalary ? 'Salaried employees' : 'Employees'}</Title3>
        <div className={styles.headerActions}>
          <Button appearance="subtle" onClick={() => setShowDeactivated((v) => !v)}>
            {showDeactivated ? 'Hide deactivated' : 'Show deactivated'}
          </Button>
          <Button appearance="secondary" onClick={() => void handleSync()} disabled={syncing}>
            {syncing ? <Spinner size="tiny" /> : 'Sync from NetSuite'}
          </Button>
          {isSalary ? null : (
            <Button appearance="primary" onClick={() => setAddOpen(true)}>
              Add employee
            </Button>
          )}
        </div>
      </div>

      <Body1>
        {visibleRows.length} employee{visibleRows.length === 1 ? '' : 's'} · {noWageCount} with no
        wage earning in NetSuite
      </Body1>

      {syncMessage !== null ? (
        <div className={styles.banner} role="status">
          <Text>{syncMessage}</Text>
        </div>
      ) : null}

      {error !== null ? (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <Spinner label="Loading employees…" />
      ) : (
        <DataTable
          columns={columns}
          rows={visibleRows}
          getRowId={(e) => e.id}
          emptyMessage={
            isSalary
              ? 'No salaried employees. Run "Sync from NetSuite" to load them.'
              : 'No employees yet. Add one to get started.'
          }
        />
      )}

      {/*
        Carried over from the retired Employee-mapping tab — the only surface where
        a manual Entra-login → employee link can be set. Rendered once (on the
        clockable roster, not duplicated onto Salary) and Admin-only, matching the
        Admin-gated endpoints behind it.
      */}
      {!isSalary && isAdmin ? (
        <div className={styles.section}>
          <EntraLinksSection />
        </div>
      ) : null}

      <SideBlade open={addOpen} onClose={closeAdd} title="Add employee">
        <form
          className={styles.form}
          onSubmit={(ev) => {
            ev.preventDefault();
            if (canAdd) {
              void handleAdd();
            }
          }}
        >
          <Field label="Display name" required>
            <Input
              value={name}
              onChange={(_, d) => setName(d.value)}
              disabled={addSaving}
              placeholder="Jane Doe"
            />
          </Field>
          <Field label="NetSuite employee id" hint="Optional until the directory is synced.">
            <Input value={nsId} onChange={(_, d) => setNsId(d.value)} disabled={addSaving} />
          </Field>
          {addError !== null ? (
            <Text className={styles.error} role="alert">
              {addError}
            </Text>
          ) : null}
          <div className={styles.actions}>
            <Button appearance="secondary" onClick={closeAdd} disabled={addSaving}>
              Cancel
            </Button>
            <Button appearance="primary" type="submit" disabled={!canAdd}>
              {addSaving ? <Spinner size="tiny" /> : 'Add'}
            </Button>
          </div>
        </form>
      </SideBlade>

      <SideBlade
        open={pinTarget !== null}
        onClose={closePin}
        title={pinTarget === null ? '' : `Set PIN — ${pinTarget.displayName}`}
        size="small"
      >
        {pinTarget !== null ? (
          pinDone ? (
            <div className={styles.form}>
              <Text className={styles.ok} role="status">
                PIN set for {pinTarget.displayName}. It is stored hashed and is never shown.
              </Text>
              <div className={styles.actions}>
                <Button appearance="primary" onClick={closePin}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <form
              className={styles.form}
              onSubmit={(ev) => {
                ev.preventDefault();
                void handleSetPin();
              }}
            >
              <Field
                label="New PIN"
                required
                hint="4–8 digits. The PIN is never displayed after you save it."
                {...(pinInvalid
                  ? {
                      validationState: 'error' as const,
                      validationMessage: 'PIN must be 4–8 digits.',
                    }
                  : {})}
              >
                <Input
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  value={pin}
                  onChange={(_, d) => setPin(d.value)}
                  disabled={pinSaving}
                />
              </Field>
              {pinError !== null ? (
                <Text className={styles.error} role="alert">
                  {pinError}
                </Text>
              ) : null}
              <div className={styles.actions}>
                <Button appearance="secondary" onClick={closePin} disabled={pinSaving}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  type="submit"
                  disabled={pinSaving || !PIN_PATTERN.test(pin)}
                >
                  {pinSaving ? <Spinner size="tiny" /> : 'Set PIN'}
                </Button>
              </div>
            </form>
          )
        ) : null}
      </SideBlade>

      <SideBlade
        open={mapTarget !== null}
        onClose={closeMap}
        title={mapTarget === null ? '' : `Map to NetSuite — ${mapTarget.displayName}`}
      >
        {mapTarget !== null ? (
          <form
            className={styles.form}
            onSubmit={(ev) => {
              ev.preventDefault();
              void handleSaveMap();
            }}
          >
            <Field
              label="NetSuite employee id"
              hint="The NS employee master is HR-locked, so this is entered manually. Leave blank to clear."
            >
              <Input
                value={mapNsId}
                onChange={(_, d) => setMapNsId(d.value)}
                disabled={mapSaving}
              />
            </Field>
            <Field label="Payroll item" hint="Earning type from the synced NetSuite payroll list.">
              <Dropdown
                value={
                  payrollLabel(payrollItems, mapPayrollId) === ''
                    ? 'None'
                    : payrollLabel(payrollItems, mapPayrollId)
                }
                selectedOptions={[mapPayrollId]}
                onOptionSelect={(_, d) => setMapPayrollId(d.optionValue ?? '')}
                disabled={mapSaving}
              >
                <Option value="" text="None">
                  None
                </Option>
                {payrollOptions.map((o) => (
                  <Option key={o.value} value={o.value} text={o.label}>
                    {o.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {mapError !== null ? (
              <Text className={styles.error} role="alert">
                {mapError}
              </Text>
            ) : null}
            {mapDeptNote !== null ? (
              <Text className={styles.ok} role="status">
                {mapDeptNote}
              </Text>
            ) : null}
            <div className={styles.actions}>
              <Button appearance="secondary" onClick={closeMap} disabled={mapSaving}>
                {mapDeptNote !== null ? 'Close' : 'Cancel'}
              </Button>
              <Button appearance="primary" type="submit" disabled={mapSaving}>
                {mapSaving ? <Spinner size="tiny" /> : 'Save mapping'}
              </Button>
            </div>
          </form>
        ) : null}
      </SideBlade>

      <SideBlade
        open={splitTarget !== null}
        onClose={closeSplit}
        title={splitTarget === null ? '' : `Departments — ${splitTarget.displayName}`}
      >
        {splitTarget !== null ? (
          splitLoading ? (
            <Spinner label="Loading…" />
          ) : (
            <div className={styles.form}>
              <Text>
                Assign the departments this employee may clock in at. They can only punch on the
                iPads for their assigned departments, and their time records to whichever department
                they actually clock at.
              </Text>
              <Checkbox
                label="Split pay across departments by percentage"
                checked={splitEnabled}
                disabled={splitSaving}
                onChange={(_, d) => toggleSplit(d.checked === true)}
              />
              {splitEnabled ? (
                <>
                  <Text>
                    {splitTarget.payType === 'salary'
                      ? 'With split on, this employee’s salary is divided by these factors across departments on the paycheck, instead of landing entirely on their home department.'
                      : 'With split on, the hours for the day are divided by these factors when posting to NetSuite instead of following the actual clock.'}{' '}
                    Two or more departments, and the percentages must total 100 (two decimals
                    allowed, so an even three-way split is exact).
                  </Text>
                  {splitRows.map((r, i) => (
                    <div key={i} className={styles.splitRow}>
                      <Dropdown
                        className={styles.splitDept}
                        value={r.departmentId === '' ? '' : deptName(r.departmentId)}
                        selectedOptions={r.departmentId === '' ? [] : [r.departmentId]}
                        placeholder="Select a department"
                        disabled={splitSaving}
                        onOptionSelect={(_, d) =>
                          setSplitRows((prev) =>
                            prev.map((row, idx) =>
                              idx === i ? { ...row, departmentId: d.optionValue ?? '' } : row
                            )
                          )
                        }
                      >
                        {departments.map((dp) => (
                          <Option key={dp.id} value={dp.id} text={dp.name}>
                            {dp.name}
                          </Option>
                        ))}
                      </Dropdown>
                      <Input
                        className={styles.pctInput}
                        type="number"
                        min={0.01}
                        max={100}
                        step={0.01}
                        contentAfter="%"
                        disabled={splitSaving}
                        value={r.percent === 0 ? '' : String(r.percent)}
                        onChange={(_, d) =>
                          setSplitRows((prev) =>
                            prev.map((row, idx) =>
                              // NOT truncated to a whole number any more: an exact
                              // three-way split needs 33.33 / 33.33 / 33.34.
                              idx === i ? { ...row, percent: Number(d.value) || 0 } : row
                            )
                          )
                        }
                      />
                      {splitRows.length > 2 ? (
                        <Button
                          size="small"
                          appearance="subtle"
                          disabled={splitSaving}
                          onClick={() => removeSplitRow(i)}
                          aria-label={`Remove row ${String(i + 1)}`}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                  ))}
                  <div>
                    <Button size="small" onClick={addSplitRow} disabled={splitSaving}>
                      Add department
                    </Button>
                  </div>
                  <Text className={splitProblem === null ? styles.ok : styles.error}>
                    Total: {splitSum.toFixed(2)}%{splitProblem === null ? '' : ` — ${splitProblem}`}
                  </Text>
                </>
              ) : (
                <Field
                  label="Assigned departments"
                  hint="At least one is required to clock in. Leave empty and the employee cannot clock in anywhere."
                >
                  <Dropdown
                    multiselect
                    selectedOptions={memberDeptIds}
                    value={
                      memberDeptIds.length === 0
                        ? ''
                        : memberDeptIds
                            .map((idv) => deptName(idv))
                            .filter((n) => n !== '')
                            .join(', ')
                    }
                    placeholder="Select departments"
                    disabled={splitSaving}
                    onOptionSelect={(_, d) => setMemberDeptIds(d.selectedOptions)}
                  >
                    {departments.map((dp) => (
                      <Option key={dp.id} value={dp.id} text={dp.name}>
                        {dp.name}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              )}
              {splitError !== null ? (
                <Text className={styles.error} role="alert">
                  {splitError}
                </Text>
              ) : null}
              <div className={styles.actions}>
                <Button appearance="secondary" onClick={closeSplit} disabled={splitSaving}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => void handleSaveSplit()}
                  disabled={!splitValid || splitSaving}
                >
                  {splitSaving ? <Spinner size="tiny" /> : 'Save'}
                </Button>
              </div>
            </div>
          )
        ) : null}
      </SideBlade>
    </div>
  );
}
